import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';

const ResumeDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchItem = async () => {
            try {
                const res = await fetch(`https://spaceportfolio-api.rajansudhan0.workers.dev/api/resume/${slug}`);
                if (!res.ok) throw new Error('Item not found');
                const data = await res.json();
                setItem(data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchItem();
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen bg-background text-text-primary">
                <Header />
                <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-accent"></div>
                </div>
                <Footer />
            </div>
        );
    }

    if (!item) {
        return (
            <div className="min-h-screen bg-background text-text-primary">
                <Helmet>
                    <meta name="robots" content="noindex" />
                    <title>Resume Item Not Found | Sudhan R</title>
                </Helmet>
                <Header />
                <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
                    <Icon name="AlertCircle" size={48} className="text-red-400" />
                    <h2 className="text-2xl font-bold">Resume Item Not Found</h2>
                    <Link to="/resume" className="text-accent hover:underline">Return to Resume</Link>
                </div>
                <Footer />
            </div>
        );
    }

    const { title, organization, location, duration, date_value, badge, description, detailedContent, achievements, technologies, type } = item;

    // Helper to format detailed content preserving line breaks
    const formatContent = (content) => {
        if (!content) return '';
        // If content contains HTML tags (basic check), return as is
        if (typeof content !== 'string') return '';
        if (/<[a-z][\s\S]*>/i.test(content)) return content;
        return content.split('\n\n').map(p => `<p class="mb-4">${p.replace(/\n/g, '<br />')}</p>`).join('');
    };

    // Determine specific schema.org types based on resume item category
    let schemaType = "EducationalOccupationalCredential";
    if (type === 'experience') {
        schemaType = "WorkBasedProgram";
    }

    const seoSchema = {
        "@context": "https://schema.org",
        "@type": schemaType,
        "name": title,
        "description": description || `Details regarding ${title} at ${organization}`,
        "provider": {
            "@type": "Organization",
            "name": organization || "Independent",
            "image": typeof badge === 'string' && badge.startsWith('http') ? badge : undefined
        },
        "url": `https://sudhan.live/resume/${slug}`
    };

    // Add specific fields based on schema type
    if (schemaType === "WorkBasedProgram") {
        seoSchema.occupationalCategory = "Technology";
        if (typeof date_value === 'string') {
            const splitDate = date_value.split('-');
            seoSchema.startDate = splitDate[0]?.trim() || "";
            seoSchema.endDate = splitDate[1]?.trim() || "Present";
        }
    } else {
        seoSchema.credentialCategory = type === "education" ? "Degree" : "Certificate";
        seoSchema.recognizedBy = {
            "@type": "Organization",
            "name": organization
        };
    }

    return (
        <div className="min-h-screen bg-background text-text-primary selection:bg-accent/30 selection:text-white">
            <Helmet>
                <title>{title} | {organization} | Sudhan R Resume</title>
                <meta name="description" content={description || `Explore Sudhan R's detailed credential: ${title} from ${organization}.`} />
                
                <meta property="og:title" content={`${title} at ${organization} | Sudhan R`} />
                <meta property="og:description" content={description || `View my portfolio and resume details for ${title}.`} />
                <meta property="og:type" content="profile" />
                <meta property="og:url" content={`https://sudhan.live/resume/${slug}`} />
                {typeof badge === 'string' && badge.startsWith('http') && <meta property="og:image" content={badge} />}

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`${title} at ${organization}`} />
                <meta name="twitter:description" content={description || `View my portfolio and resume details for ${title}.`} />

                <link rel="canonical" href={`https://sudhan.live/resume/${slug}`} />

                <script type="application/ld+json">
                    {JSON.stringify(seoSchema)}
                </script>
            </Helmet>

            <Header />

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24">
                <button 
                    onClick={() => navigate(-1)}
                    aria-label="Return to the main resume page"
                    title="Back to Resume"
                    className="inline-flex items-center gap-2 text-text-secondary hover:text-accent font-medium mb-12 transition-smooth group"
                >
                    <Icon name="ArrowLeft" size={20} className="group-hover:-translate-x-1 transition-transform" />
                    Back to Full Resume
                </button>

                <article className="bg-surface/50 backdrop-blur-xl border border-white/10 rounded-3xl p-8 lg:p-12 shadow-2xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

                    <header className="mb-10 relative z-10">
                        <div className="flex flex-wrap items-center gap-3 mb-8">
                            <span className={`px-4 py-1.5 rounded-full text-sm font-semibold tracking-wide uppercase ${
                                type === 'education' ? 'bg-success/20 text-success-light border border-success/30' : 'bg-primary-900/50 text-primary-300 border border-primary-700/50'
                            }`}>
                                {type}
                            </span>
                            {/* Text Badge Fallback */}
                            {typeof badge === 'string' && badge && !badge.startsWith('http') && (
                                <span className="bg-white/5 border border-white/10 px-4 py-1.5 rounded-full text-sm font-medium text-text-secondary">
                                    {badge}
                                </span>
                            )}
                        </div>

                        <div className="flex flex-col md:flex-row md:items-center gap-6 lg:gap-8">
                            {/* Prominent Logo Display */}
                            {typeof badge === 'string' && badge.startsWith('http') && (
                                <div className="w-20 h-20 md:w-28 md:h-28 bg-white rounded-2xl flex items-center justify-center p-3 overflow-hidden shadow-lg border border-white/20 flex-shrink-0">
                                    <Image src={badge} alt={`${organization} logo`} className="w-full h-full object-contain" />
                                </div>
                            )}

                            <div className="flex-1 min-w-0">
                                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-white/90 to-white/70 mb-4 leading-tight">
                                    {title}
                                </h1>

                                <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-text-secondary font-medium">
                                    <span className="flex items-center gap-2 text-accent-light text-lg">
                                        <Icon name="Briefcase" size={20} />
                                        {organization}
                                    </span>
                                    {(location) && (
                                        <span className="flex items-center gap-2">
                                            <Icon name="MapPin" size={18} />
                                            {location}
                                        </span>
                                    )}
                                    {(duration || date_value) && (
                                        typeof duration === 'string' && duration.startsWith('http') ? (
                                            <div className="flex items-center gap-4">
                                                {date_value && (
                                                    <span className="flex items-center gap-2">
                                                        <Icon name="Calendar" size={18} />
                                                        {date_value}
                                                    </span>
                                                )}
                                                <a 
                                                    href={duration} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer" 
                                                    className="flex items-center gap-2 text-accent-light bg-accent/10 border border-accent/20 hover:bg-accent/20 px-4 py-1.5 rounded-full transition-smooth font-bold"
                                                    aria-label={`View official credential for ${title} from ${organization}`}
                                                    title="Verify Credential"
                                                >
                                                    <Icon name="ExternalLink" size={16} />
                                                    Show Credential
                                                </a>
                                            </div>
                                        ) : (
                                            <span className="flex items-center gap-2">
                                                <Icon name="Calendar" size={18} />
                                                {duration || date_value}
                                            </span>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    </header>

                    {description && (
                        <div className="mb-10 p-6 bg-white/5 border-l-4 border-accent rounded-r-2xl">
                            <p className="text-lg text-text-secondary leading-relaxed italic">
                                "{description}"
                            </p>
                        </div>
                    )}

                    {detailedContent && (
                        <div 
                            className="prose prose-invert prose-lg max-w-none prose-headings:font-heading prose-headings:font-bold prose-a:text-accent hover:prose-a:text-accent-light prose-p:leading-relaxed prose-p:text-text-secondary mb-12"
                            dangerouslySetInnerHTML={{ __html: formatContent(detailedContent) }}
                        />
                    )}

                    <div className="grid md:grid-cols-2 gap-8 border-t border-white/10 pt-10">
                        {achievements && achievements.length > 0 && (
                            <div>
                                <h3 className="text-xl font-heading font-bold text-text-primary flex items-center gap-2 mb-6">
                                    <Icon name="Award" className="text-accent" />
                                    Key Highlights
                                </h3>
                                <ul className="space-y-4">
                                    {achievements.map((ach, idx) => (
                                        <li key={idx} className="flex items-start gap-3">
                                            <div className="w-2 h-2 bg-success-light rounded-full mt-2.5 flex-shrink-0 shadow-[0_0_8px_rgba(34,197,94,0.4)]"></div>
                                            <span className="text-text-secondary leading-relaxed">{ach}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {technologies && technologies.length > 0 && (
                            <div>
                                <h3 className="text-xl font-heading font-bold text-text-primary flex items-center gap-2 mb-6">
                                    <Icon name="Cpu" className="text-accent" />
                                    {type === 'education' ? 'Coursework & Skills' : 'Technologies Used'}
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {technologies.map((tech, idx) => (
                                        <span key={idx} className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm font-medium text-accent-light shadow-sm">
                                            {tech}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </article>
            </main>

            <Footer />
        </div>
    );
};

export default ResumeDetail;
